import { useCallback, useEffect, useRef } from "react";
import { useShallow } from "zustand/react/shallow";

import useAssistantPanelStore from "./useStore";

const useFloatingDrag = () => {
  const { floatingPosition, setFloatingPosition } = useAssistantPanelStore(
    useShallow((state) => ({
      floatingPosition: state.floatingPosition,
      setFloatingPosition: state.setFloatingPosition,
    }))
  );

  const isDragging = useRef(false);
  const startPos = useRef({ x: 0, y: 0 });
  const initialPosition = useRef({ x: 0, y: 0 });
  const rafId = useRef<number | null>(null);
  const pointerId = useRef<number | null>(null);
  const targetElement = useRef<HTMLElement | null>(null);

  const onDragStart = useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== 0 || e.isPrimary === false) {
        return;
      }

      // Skip if clicking on buttons
      if ((e.target as HTMLElement).closest("button")) {
        return;
      }

      e.preventDefault();
      const target = e.currentTarget as HTMLElement;
      target.setPointerCapture(e.pointerId);
      pointerId.current = e.pointerId;
      targetElement.current = target;

      isDragging.current = true;
      startPos.current = { x: e.clientX, y: e.clientY };
      initialPosition.current = { x: floatingPosition.x, y: floatingPosition.y };
      document.body.style.userSelect = "none";
    },
    [floatingPosition]
  );

  useEffect(() => {
    const handleMove = (e: PointerEvent) => {
      if (!isDragging.current) return;

      e.preventDefault();

      if (rafId.current) return;

      rafId.current = requestAnimationFrame(() => {
        const deltaX = e.clientX - startPos.current.x;
        const deltaY = e.clientY - startPos.current.y;

        setFloatingPosition({
          x: initialPosition.current.x + deltaX,
          y: initialPosition.current.y + deltaY,
        });
        rafId.current = null;
      });
    };

    const handleEnd = () => {
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
      if (isDragging.current) {
        if (targetElement.current && pointerId.current !== null) {
          targetElement.current.releasePointerCapture(pointerId.current);
          pointerId.current = null;
          targetElement.current = null;
        }

        isDragging.current = false;
        document.body.style.userSelect = "";
      }
    };

    // Keep floating panel within viewport when window resizes
    const handleResize = () => {
      // Re-apply position - setFloatingPosition will clamp to new viewport bounds
      setFloatingPosition(floatingPosition);
    };

    document.addEventListener("pointermove", handleMove);
    document.addEventListener("pointerup", handleEnd);
    document.addEventListener("pointercancel", handleEnd);
    window.addEventListener("resize", handleResize);

    return () => {
      document.removeEventListener("pointermove", handleMove);
      document.removeEventListener("pointerup", handleEnd);
      document.removeEventListener("pointercancel", handleEnd);
      window.removeEventListener("resize", handleResize);
    };
  }, [floatingPosition, setFloatingPosition]);

  return { onDragStart };
};

export default useFloatingDrag;
